import { Bell, Download, FileText } from 'lucide-react'
import { useState } from 'react'
import { ReportClientPanel } from '@/components/reports/ReportClientPanel'
import { ReportClientsTable } from '@/components/reports/ReportClientsTable'
import { Card, CardHeader } from '@/components/ui/Card'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { downloadLedger, useReportsOverview, useUpdateReportSettings } from '@/lib/report-api'
import type { HoursReportClient, HoursReportProject } from '@/lib/report-types'

function formatTimestamp(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
}

export function ReportsPage() {
  const overviewQuery = useReportsOverview()
  const updateSettings = useUpdateReportSettings()
  const [panelTarget, setPanelTarget] = useState<{ clientId: string; projectId: string } | null>(null)
  const [ledgerYear, setLedgerYear] = useState(() => new Date().getFullYear())
  const [exporting, setExporting] = useState(false)

  const clients = overviewQuery.data?.clients ?? []
  const reminders = overviewQuery.data?.reminders ?? []
  const panelClient = clients.find((c) => c.clientId === panelTarget?.clientId) ?? null
  const panelProject = panelClient?.projects.find((p) => p.projectId === panelTarget?.projectId) ?? null

  const currentYear = new Date().getFullYear()
  const yearOptions = Array.from({ length: 6 }, (_, i) => currentYear + 1 - i)

  async function exportLedger() {
    setExporting(true)
    try {
      await downloadLedger(ledgerYear, 'csv')
    } finally {
      setExporting(false)
    }
  }

  function toggleWeekly(client: HoursReportClient, next: boolean) {
    updateSettings.mutate({ clientId: client.clientId, input: { weeklyEnabled: next } })
  }

  function openPanel(client: HoursReportClient, project: HoursReportProject) {
    setPanelTarget({ clientId: client.clientId, projectId: project.projectId })
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rapportages"
        description="Wekelijkse urenrapportages per project en het remindersoverzicht voor ontbrekende uren."
        actions={
          <>
            <Select
              value={String(ledgerYear)}
              onValueChange={(v) => setLedgerYear(Number(v))}
              options={yearOptions.map((y) => ({ value: String(y), label: String(y) }))}
              className="w-24"
            />
            <button type="button" className="btn-secondary" onClick={exportLedger} disabled={exporting}>
              <Download className="h-4 w-4" />
              Grootboek exporteren
            </button>
          </>
        }
      />

      <Card>
        <CardHeader title="Klantrapportages" subtitle="Een rapportmail per project; ontvangers gelden voor de hele klant of per project" action={<FileText className="h-4 w-4 text-ink-faint" />} />
        <div className="mt-4">
          {overviewQuery.isError ? (
            <ErrorState compact error={overviewQuery.error} onRetry={() => overviewQuery.refetch()} />
          ) : overviewQuery.isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : clients.length === 0 ? (
            <EmptyState icon={FileText} title="Geen klanten" description="Er zijn nog geen actieve klanten om te rapporteren." />
          ) : (
            <ReportClientsTable
              clients={clients}
              onView={openPanel}
              onToggleWeekly={toggleWeekly}
              togglingId={updateSettings.isPending ? updateSettings.variables?.clientId ?? null : null}
            />
          )}
        </div>
      </Card>

      <Card>
        <CardHeader title="Reminders" subtitle="Wie kreeg wanneer een week-akkoord-reminder (week nog niet afgesloten)" action={<Bell className="h-4 w-4 text-ink-faint" />} />
        {overviewQuery.isLoading ? (
          <Skeleton className="mt-4 h-24 w-full" />
        ) : reminders.length === 0 ? (
          <p className="mt-4 text-sm text-ink-faint">Nog geen reminders verstuurd.</p>
        ) : (
          <div className="mt-4 divide-y divide-hairline">
            {reminders.map((reminder) => (
              <div key={reminder.id} className="flex items-center justify-between py-2 text-sm">
                <span className="font-medium text-ink">{reminder.personName}</span>
                <span className="text-ink-faint">
                  {reminder.isoWeek} · {formatTimestamp(reminder.sentAt)} · {reminder.channel}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {panelClient && panelProject ? (
        <ReportClientPanel
          key={panelClient.clientId}
          client={panelClient}
          project={panelProject}
          onSelectProject={(projectId) => setPanelTarget({ clientId: panelClient.clientId, projectId })}
          onClose={() => setPanelTarget(null)}
        />
      ) : null}
    </div>
  )
}
